import React from 'react';
import { Box, Typography } from '@mui/material';
import { ContactName, UserContact } from '@uplift/types';
import { useFeatureFlag, useL10n } from '@uplift/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useNotificationPreferencesQuery, useNotificationPreferencesMutation, userContactsQueryKey, usePublicICSSubscriptionQuery } from '@uplift/queries';
import { Button, ClickToCopy, ConfirmationProvider, EditableSectionContainer, EditableSectionRow, palette, PhoneVerificationModal, PublicICSSubscriptionModal, ToggleSwitch, useConfirmation, useEditableSectionsContext } from '@uplift/ui-v2';

const ICSSubscriptionSectionRow: React.VFC = () => {
  const l10n = useL10n();
  const [ calendarModalOpen, setCalendarModalOpen ] = React.useState(false);

  const { data: subscription } = usePublicICSSubscriptionQuery();
  const subscriptionUrl = subscription?.subscriptionUrl;
  const isSubscribed = Boolean(subscriptionUrl);
  const displayUrl = subscriptionUrl && subscriptionUrl.length > 40 ? `${subscriptionUrl.slice(0, 40)}…` : subscriptionUrl;

  return (
    <>
      <EditableSectionRow
        rowKey="calendarSubscription"
        label={l10n.accountSettings.notifications.yourCalendar}
        kind="custom"
        actions={isSubscribed ? (
          <>
            <ClickToCopy text={subscriptionUrl ?? ''} />
            <Button color="secondary" onClick={() => setCalendarModalOpen(true)}>{l10n.accountSettings.notifications.manageCalendar}</Button>
          </>
        ) : (
          <Button color="secondary" onClick={() => setCalendarModalOpen(true)}>{l10n.accountSettings.notifications.getLink}</Button>
        )}
      >
        <Typography variant="body1">
          {isSubscribed ? displayUrl : l10n.accountSettings.notifications.calendarDescription}
        </Typography>
      </EditableSectionRow>
      <PublicICSSubscriptionModal open={calendarModalOpen} onClose={() => setCalendarModalOpen(false)} />
    </>
  );
};

export type NotificationSectionProps = {
  userId: string;
  mobileContact?: UserContact;
};

const NotificationStatus: React.FC<{ status: string; description: string }> = ({ status, description }) => (
  <Box>
    <Typography variant="body1" fontWeight={500}>{status}</Typography>
    <Typography variant="body2" color="textSecondary">{description}</Typography>
  </Box>
);


const getSmsDescription = (l10n: ReturnType<typeof useL10n>, mobileContact?: UserContact): string => {
  if (!mobileContact) {
    return l10n.accountSettings.notifications.addNumberToEnable;
  }

  if (!mobileContact.verified) {
    return l10n.accountSettings.notifications.verifyToEnable;
  }

  return l10n.accountSettings.notifications.smsReminderDescription;
};

const NotificationSectionInner: React.VFC<NotificationSectionProps> = ({ userId, mobileContact }) => {
  const l10n = useL10n();
  const { confirm } = useConfirmation();
  const queryClient = useQueryClient();
  const [ verificationModalOpen, setVerificationModalOpen ] = React.useState(false);

  const showCalendarSubscription = useFeatureFlag('USER_PUBLIC_ICS_SUBSCRIPTION');

  const { setEditKey } = useEditableSectionsContext();
  const isMobileVerified = mobileContact?.verified ?? false;
  const { data: { appointmentEmail, appointmentSms } = {} } = useNotificationPreferencesQuery(userId);
  const { mutateAsync } = useNotificationPreferencesMutation(userId);

  const smsRemindersOn = (appointmentSms ?? false) && isMobileVerified;
  const emailRemindersOn = appointmentEmail ?? false;

  const handleToggle = async (field: 'appointment_sms' | 'appointment_email', newValue: boolean) => {
    if (!newValue) {
      confirm({
        title: l10n.accountSettings.notifications.turnOffTitle,
        description: l10n.accountSettings.notifications.turnOffDescription,
        confirmButtonText: l10n.accountSettings.notifications.turnOffConfirm,
        onConfirm: async () => {
          await mutateAsync({ [field]: false });
        },
      });
    } else {
      await mutateAsync({ [field]: true });
    }
  };

  let textRemindersAction: React.ReactNode;

  if (!mobileContact) {
    textRemindersAction = (
      <Button variant="text" color="secondary" onClick={() => setEditKey('phone')}>
        {l10n.accountSettings.notifications.addNumber}
      </Button>
    );
  } else if (!mobileContact.verified) {
    textRemindersAction = (
      <Button variant="text" color="secondary" onClick={() => setVerificationModalOpen(true)}>
        {l10n.accountSettings.notifications.verifyNumber}
      </Button>
    );
  } else {
    textRemindersAction = (
      <ToggleSwitch
        checked={smsRemindersOn}
        offColor={(palette.error as { main: string })?.main}
        onChange={(value) => handleToggle('appointment_sms', value)}
      />
    );
  }

  return (
    <>
      <EditableSectionContainer title={l10n.accountSettings.notifications.title} rowDividers>
        <EditableSectionRow
          rowKey="textReminders"
          label={l10n.accountSettings.notifications.textReminders}
          kind="custom"
          actions={textRemindersAction}
        >
          <NotificationStatus
            status={smsRemindersOn ? l10n.general.on : l10n.general.off}
            description={getSmsDescription(l10n, mobileContact)}
          />
        </EditableSectionRow>

        <EditableSectionRow
          rowKey="emailReminders"
          label={l10n.accountSettings.notifications.emailReminders}
          kind="custom"
          actions={(
            <ToggleSwitch
              checked={emailRemindersOn}
              offColor={(palette.error as { main: string })?.main}
              onChange={(value) => handleToggle('appointment_email', value)}
            />
          )}
        >
          <NotificationStatus
            status={emailRemindersOn ? l10n.general.on : l10n.general.off}
            description={l10n.accountSettings.notifications.sessionReminderDescription}
          />
        </EditableSectionRow>

        {showCalendarSubscription && <ICSSubscriptionSectionRow />}

        <Typography variant="body2" color="textSecondary" px={3} py={2}>
          {l10n.accountSettings.notifications.bookingAlwaysSent}
        </Typography>
      </EditableSectionContainer>

      {mobileContact && (
        <PhoneVerificationModal
          open={verificationModalOpen}
          onClose={() => setVerificationModalOpen(false)}
          onSubmit={() => queryClient.invalidateQueries({ queryKey: userContactsQueryKey(userId) })}
          userId={userId}
          phone={mobileContact.value}
          contactId={mobileContact.id}
          contactName={mobileContact.name as ContactName}
        />
      )}
    </>
  );
};

export const NotificationSection: React.VFC<NotificationSectionProps> = (props) => (
  <ConfirmationProvider>
    <NotificationSectionInner {...props} />
  </ConfirmationProvider>
);
